/**
 * Реестр индикаторов.
 *
 * Отдельный слой поверх списка определений: график, меню и стор
 * обращаются сюда, а не к массиву напрямую. Когда появятся
 * пользовательские индикаторы, регистрация добавится здесь и больше
 * нигде.
 */

import { INDICATOR_DEFINITIONS, defaultParams } from './definitions.js';

// Реестр — единственная дверь к определениям: стор и меню спрашивают
// умолчания здесь, а не у массива напрямую.
export { defaultParams };
import type {
  IndicatorAuthor,
  IndicatorDefinition,
  IndicatorInstance,
  IndicatorParams,
} from './types.js';

const BY_ID = new Map<string, IndicatorDefinition>(
  INDICATOR_DEFINITIONS.map((definition) => [definition.id, definition]),
);

export function listIndicators(): readonly IndicatorDefinition[] {
  return INDICATOR_DEFINITIONS;
}

/**
 * Индикаторы одного авторства.
 *
 * Регистрация нового авторского индикатора — одна запись в списке
 * определений с `author: 'specter'`. Ни меню, ни график, ни стор об
 * этом знать не должны: они спрашивают реестр.
 */
export function indicatorsBy(author: IndicatorAuthor): readonly IndicatorDefinition[] {
  return INDICATOR_DEFINITIONS.filter((definition) => (definition.author ?? 'base') === author);
}

export function findIndicator(id: string): IndicatorDefinition | undefined {
  return BY_ID.get(id);
}

let counter = 0;

/** Создаёт экземпляр с параметрами и цветами по умолчанию. */
export function createInstance(definitionId: string): IndicatorInstance | null {
  const definition = BY_ID.get(definitionId);
  if (!definition) return null;

  counter += 1;
  return {
    instanceId: `${definitionId}-${Date.now().toString(36)}-${counter}`,
    definitionId,
    params: defaultParams(definition),
    colors: {},
    visible: true,
  };
}

/**
 * Цвет выхода.
 *
 * Три источника, в порядке убывания старшинства. Настройка-цвет с тем же
 * ключом, что у выхода, — это способ индикатора поставить выбор цвета
 * туда, где ему место: рядом с галочкой, которая эту линию включает, а
 * не в общем блоке под двумя десятками других настроек. Дальше — цвет,
 * выбранный человеком в самом блоке. И наконец умолчание определения.
 */
export function colorOf(
  definition: IndicatorDefinition,
  instance: IndicatorInstance,
  outputKey: string,
  params?: IndicatorParams,
): string {
  if (params && hasColorParam(definition, outputKey)) {
    const value = params[outputKey];
    if (typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value)) return value;
  }

  const custom = instance.colors[outputKey];
  if (custom) return custom;
  const output = definition.outputs.find((o) => o.key === outputKey);
  return output?.defaultColor ?? '#79818F';
}

/** Есть ли у определения настройка-цвет, названная как выход. */
export function hasColorParam(definition: IndicatorDefinition, outputKey: string): boolean {
  return definition.params.some((spec) => spec.type === 'color' && spec.key === outputKey);
}

/**
 * Приводит сохранённые параметры к текущему определению.
 *
 * Настройки живут дольше кода: пользователь мог сохранить индикатор,
 * а мы потом переименовали параметр или сузили диапазон. Молча
 * подставить NaN в период — значит нарисовать пустую линию без объяснений.
 */
export function sanitizeParams(
  definition: IndicatorDefinition,
  stored: IndicatorParams,
): IndicatorParams {
  const params: Record<string, number | string | boolean> = {};
  for (const spec of definition.params) {
    const value = stored[spec.key];
    if (spec.type === 'number') {
      const numeric = typeof value === 'number' ? value : Number(value);
      params[spec.key] = Number.isFinite(numeric)
        ? Math.min(Math.max(numeric, spec.min), spec.max)
        : spec.default;
    } else if (spec.type === 'boolean') {
      params[spec.key] = typeof value === 'boolean' ? value : spec.default;
    } else if (spec.type === 'color') {
      // Цвет проверяется по форме: в профиль могло попасть что угодно, а
      // мусор в заливке — это невидимые фигуры без всякого объяснения.
      params[spec.key] =
        typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : spec.default;
    } else if (spec.type === 'days') {
      // Только цифры 1–7, без повторов и по порядку; запертые дни вычёркиваются.
      const locked = new Set(spec.locked?.days ?? '');
      const text = typeof value === 'string' ? value : spec.default;
      params[spec.key] = [...new Set([...text].filter((char) => /[1-7]/.test(char) && !locked.has(char)))]
        .sort()
        .join('');
    } else if (spec.type === 'select') {
      // Значение из закрытого списка: то, чего в нём больше нет,
      // молча превратилось бы в неработающую настройку.
      const known = spec.options.some((option) => option.value === value);
      params[spec.key] = known ? (value as string) : spec.default;
    } else {
      params[spec.key] = typeof value === 'string' ? value : spec.default;
    }
  }
  return params;
}
